import { Button, Input } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { documentsTemplateDepartmentsLookupUrl } from "../api/client";
import { useTemplatesQuery } from "../api/queries";
import { DocumentPortalTour } from "../tour/DocumentPortalTour";
import { templatesCenterTourSteps } from "../tour/documentPortalTourSteps";
import { useDocumentPortalTour } from "../tour/useDocumentPortalTour";
import { TemplateDocumentCard } from "../ui/TemplateDocumentCard";

export function TemplatesCenterPage() {
  const navigate = useNavigate();
  const tour = useDocumentPortalTour("doc-templates-center-tour-completed");
  const searchParams = useSearch({ from: "/app/documents/templates" }) as {
    departmentId?: string;
    search?: string;
    page?: number;
  };
  const [localSearch, setLocalSearch] = useState(searchParams.search ?? "");

  const departmentId = searchParams.departmentId ?? "";
  const page = Math.max(1, searchParams.page ?? 1);
  const limit = 6;
  const offset = (page - 1) * limit;

  const query = useTemplatesQuery({
    departmentId: departmentId || undefined,
    search: searchParams.search || undefined,
    limit,
    offset,
  });

  const data = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const lookupUrl = documentsTemplateDepartmentsLookupUrl();

  const buildSearch = (overrides: {
    departmentId?: string;
    search?: string;
    page?: number;
  }) => ({
    departmentId:
      overrides.departmentId !== undefined
        ? overrides.departmentId || undefined
        : searchParams.departmentId,
    search:
      overrides.search !== undefined
        ? overrides.search || undefined
        : searchParams.search,
    page: overrides.page ?? searchParams.page ?? 1,
  });

  const handleDepartmentChange = (val: string) => {
    navigate({
      to: "/app/documents/templates",
      search: buildSearch({
        departmentId: val,
        page: 1,
      }),
    });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({
      to: "/app/documents/templates",
      search: buildSearch({
        search: localSearch,
        page: 1,
      }),
    });
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-2 flex justify-between space-y-4">
          <div className="">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-2xl tracking-tight">
                  ຮ່າງເອກະສານ
                </h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-4" />
                </Button>
              </div>
            </div>
            <p className="mt-0.5 font-medium text-muted-foreground">
              ເອກະສານແມ່ແບບພ້ອມລາຍລະອຽດແລະດາວໂຫລດ
            </p>
            <div className="mt-2 h-1 w-16 rounded-full bg-black" />
          </div>
          <div>
            <Button
              variant="default"
              className="w-full border-black font-medium hover:bg-black hover:text-white"
              onClick={() => navigate({ to: "/app/dashboard" })}
            >
              <ArrowLeft className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        {/* Filters & search */}
        <div
          data-tourid="doc-templates-filters"
          className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="w-full sm:max-w-xs">
            <InfiniteCombobox<LookupItem>
              queryKey={["documents", "templates", "departments-lookup"]}
              className="h-9"
              value={departmentId}
              onValueChange={handleDepartmentChange}
              queryFn={(args) => fetchLookupForInfinite(lookupUrl, args)}
              preloadQueryFn={(id) => hydrateLookupItem(lookupUrl, id)}
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              clearable
              placeholder="ກອງຕາມແຜ່ນການ..."
            />
          </div>
          <form onSubmit={handleSearchSubmit} className="flex w-full sm:w-64">
            <div className="relative flex-1">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="ຄົ້ນຫາຮ່າງເອກະສານ..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </form>
        </div>

        {/* Document grid */}
        <div
          data-tourid="doc-templates-grid"
          className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {query.isLoading
            ? Array.from({ length: 6 }, (_, i) => `skeleton-template-${i}`).map(
                (key) => (
                  <div
                    key={key}
                    className="h-48 animate-pulse rounded-xl bg-muted"
                  />
                ),
              )
            : data.map((item) => (
                <TemplateDocumentCard key={item.id} item={item} />
              ))}
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-muted-foreground text-sm">
            ສະແດງ {data.length} ຈາກ {total} ເອກະສານ
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              disabled={page <= 1}
              onClick={() =>
                navigate({
                  to: "/app/documents/templates",
                  search: buildSearch({ page: page - 1 }),
                })
              }
            >
              ‹
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = i + 1;
              return (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  size="icon"
                  className={`size-9 ${page === p ? "bg-black text-white" : ""}`}
                  onClick={() =>
                    navigate({
                      to: "/app/documents/templates",
                      search: buildSearch({ page: p }),
                    })
                  }
                >
                  {p}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              disabled={page >= totalPages}
              onClick={() =>
                navigate({
                  to: "/app/documents/templates",
                  search: buildSearch({ page: page + 1 }),
                })
              }
            >
              ›
            </Button>
          </div>
        </div>
        <DocumentPortalTour
          steps={templatesCenterTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
